import { pickAt } from '../tools/picking';
import * as THREE from 'three';
import { Model } from '../core/model';
import { Unit } from '../core/units';
import { AppContext, UiBridge, PreviewBridge } from './context';
import { SelectionState } from './selection';
import { History } from '../commands/history';
import { OrbitController } from '../camera/orbitController';
import { TouchNavigator } from '../camera/touchNavigation';
import { SceneManager } from '../render/sceneManager';
import { ModelRenderer } from '../render/modelRenderer';
import { PreviewRenderer } from '../render/preview';
import { VanRenderer } from '../render/vanRenderer';
import { InferenceEngine } from '../inference/inference';
import { ToolManager } from '../tools/toolManager';
import { ToolPointer } from '../tools/tool';
import { eraseEdges, setEdgeLength, connectedFaceComponent, scaleAboutAnchor, extrudeFace, moveVertices, flipEntities } from '../core/ops';
import { dist, vec, sub } from '../core/vec';
import type { Vec3 } from '../core/vec';
import { SelectionInfo } from '../ui/entityInfo';
import { SelectTool } from '../tools/selectTool';
import { LineTool } from '../tools/lineTool';
import { RectangleTool } from '../tools/rectangleTool';
import { CircleTool } from '../tools/circleTool';
import { ArcTool } from '../tools/arcTool';
import { PushPullTool } from '../tools/pushPullTool';
import { MoveTool } from '../tools/moveTool';
import { RotateTool } from '../tools/rotateTool';
import { ScaleTool } from '../tools/scaleTool';
import { OffsetTool } from '../tools/offsetTool';
import { EraserTool } from '../tools/eraserTool';
import { TapeTool } from '../tools/tapeTool';
import { PaintTool } from '../tools/paintTool';
import { PlaceTool } from '../tools/placeTool';
import { OrbitTool, PanTool, ZoomTool } from '../tools/cameraTools';
import { TopBar } from '../ui/topbar';
import { Toolbar } from '../ui/toolbar';
import { StatusBar } from '../ui/statusbar';
import { Panels, LayerTarget, LayerInfo } from '../ui/panels';
import { EntityInfoPanel } from '../ui/entityInfo';
import { ShortcutOverlay } from '../ui/shortcutOverlay';
import { ShareOverlay } from '../ui/shareOverlay';
import { ParticipantsBar } from '../ui/participants';
import { FollowInviteOverlay } from '../ui/followInviteOverlay';
import { CollabManager, newRoomId, type CollabHooks } from '../collab/collab';
import { lerpPeerViewStep, peerViewEqual } from '../collab/follow';
import { PresenceRenderer } from '../collab/presenceRenderer';
import type { RoomDoc, MetaOps, PeerInfo, PeerView } from '../../server/protocol';
import { VAN_TEMPLATES, VanState, templateById } from '../vans/vans';
import { CustomFurnitureData, extractCustomTemplate } from '../furniture/furniture';
import { addCustomFurniture, listCustomFurniture, mergeCustomFurniture, removeCustomFurniture } from '../furniture/library';
import { ProjectData, saveProjectFile, openProjectFile, autosave, loadAutosave, clearAutosave } from '../io/persistence';
import { api, ApiError, type SessionUser } from '../io/api';
import { AuthScreen } from '../ui/authScreen';
import { showUpgradeNotice } from '../ui/upgradeNotice';

const TOOL_SHORTCUTS: Record<string, string> = {
  l: 'line', r: 'rectangle', c: 'circle', a: 'arc', f: 'offset',
  p: 'pushpull', m: 'move', q: 'rotate', s: 'scale',
  e: 'eraser', t: 'tape', b: 'paint', o: 'orbit', h: 'pan', z: 'zoom'
};

const VIEW_KEYS: Record<string, string> = {
  '1': 'top', '2': 'bottom', '3': 'front', '4': 'back', '5': 'left', '6': 'right', '7': 'iso'
};

/** Mindestabstand zwischen zwei Server-Speicherungen (ms) — die API soll nicht fluten. */
const SERVER_SAVE_MIN_MS = 2500;

/** Glättungsfaktor des Folgen-Modus: Anteil der Ansichts-Differenz pro Frame. */
const FOLLOW_LERP = 0.35;

/** Schlüssel für die letzte Wahl der Seitenleiste (localStorage). */
const PANELS_KEY = 'campsketch.panels.hidden';
/** Breakpoint, ab dem die Seitenleiste standardmäßig eingeklappt ist (wie im CSS). */
const PANELS_MOBILE_QUERY = '(max-width: 900px)';

/** Startparameter des Editors: gebundenes Server-Projekt oder anonymer Modus. */
export interface AppOptions {
  /** Server-Projekt (aus der Projekt-Datenbank). null = anonymer Modus (z. B. /join?room=). */
  project: { id: string; name: string } | null;
  /** Projektinhalt — Pflicht, wenn `project` gesetzt ist (vom Server geladen und validiert). */
  data?: ProjectData;
}

export class App implements AppContext {
  model = new Model();
  selection = new SelectionState();
  history = new History(100);
  scene: SceneManager;
  renderer: ModelRenderer;
  preview: PreviewBridge;
  vanRenderer = new VanRenderer();
  camera: OrbitController;
  inference: InferenceEngine;
  toolManager: ToolManager;
  ui: UiBridge;
  overlay: ShortcutOverlay;
  shareOverlay: ShareOverlay;

  /** Werkzeug zum Stellen von Möbeln aus der Bibliothek (Möbel-Panel). */
  private placeTool: PlaceTool;

  private canvas: HTMLCanvasElement;
  private statusBar: StatusBar;
  private panels: Panels;
  private entityInfo: EntityInfoPanel;
  private topBar: TopBar;
  private participantsBar: ParticipantsBar;
  private followInviteOverlay: FollowInviteOverlay;
  private previewRenderer: PreviewRenderer;
  private presenceRenderer: PresenceRenderer;
  /** Aktive Kollaborations-Session (null = lokaler Modus). */
  private collab: CollabManager | null = null;
  /** Raum-ID aus dem Share-Link, der noch bestätigt werden muss (Namenseingabe). */
  private joinRoomId: string | null = null;
  private lastPresencePickAt = 0;
  /** Folgen-Modus: Client-ID des gefolgten Teilnehmers (null = aus). */
  private followId: string | null = null;
  /** Kamera wurde im letzten Frame vom Folgen bewegt (kein Nutzer-Eingriff). */
  private followJustApplied = false;
  /** Eigene Ansicht beim letzten Frame — erkennt lokale Kamerabewegung (beendet Folgen). */
  private lastObservedView: PeerView | null = null;
  /** Rechte Seitenleiste eingeklappt (mobil standardmäßig ja, Wahl wird gespeichert). */
  private panelsHidden = false;
  /** DOM-Wurzel der rechten Seitenleiste (für das Ein-/Ausklappen). */
  private panelsRootEl: HTMLElement | null = null;
  /** Abdunkelung hinter dem mobilen Seitenleisten-Drawer. */
  private panelsBackdropEl: HTMLElement | null = null;
  private vcbBufferValue = '';
  private unitsValue: Unit = 'cm';
  private currentColorValue = '#d9cbb3';
  private van: VanState = {
    templateId: 'sprinter-l3h2',
    length: 4.7, width: 1.78, height: 1.93,
    visible: true, arches: true
  };
  private modelDirty = true;
  private needsSave = false;
  private lastSaveAt = 0;
  private lastSavedJson = '';
  /** Gebundenes Server-Projekt (null = anonymer Modus). */
  private projectBinding: { id: string; name: string } | null;
  /** Server-Speicherung: hat ungespeicherte Änderungen / läuft gerade / Retry-Timer. */
  private serverDirty = false;
  private serverSaveInFlight = false;
  private serverSaveTimer: number | null = null;
  private lastServerSaveAt = 0;
  private lastServerSavedJson = '';
  private destroyed = false;
  private rafHandle = 0;
  private tickInterval = 0;
  private readonly ac = new AbortController();
  private resizeObserver: ResizeObserver;
  private middleDrag: { x: number; y: number; startX: number; startY: number; pan: boolean } | null = null;
  private lastMiddleClickAt = 0;
  /** Touch-Gesten (Orbit/Pan/Pinch/Long-Press) — Touch-Pointer werden hierhin umgeleitet. */
  private touchNav: TouchNavigator;
  /** Linke Taste wurde auf der Arbeitsfläche gedrückt und ist noch nicht losgelassen. */
  private leftDown = false;
  /** Nächstes Loslassen ignorieren (Klick hat nur das Kontextmenü geschlossen). */
  private swallowClick = false;
  private readonly isMac = /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);

  constructor(root: HTMLElement, options: AppOptions = { project: null }) {
    this.projectBinding = options.project;
    // Der Editor besitzt den Container komplett — alte Bildschirme (z. B. die
    // Anmeldekarte nach dem Login) müssen hier verschwinden
    root.replaceChildren();
    // Layout
    this.canvas = document.createElement('canvas');
    this.canvas.className = 'viewport';

    root.className = 'app';
    const main = document.createElement('div');
    main.className = 'main';
    main.appendChild(this.canvas);

    this.scene = new SceneManager(this.canvas);
    this.camera = new OrbitController(this.canvas);
    this.camera.picker = (x, y) => this.renderer.pickPoint(x, y, this.camera.camera);
    this.inference = new InferenceEngine(this.model, this.camera);
    this.renderer = new ModelRenderer(this.model, this.selection);
    this.previewRenderer = new PreviewRenderer();
    this.preview = this.previewRenderer;
    this.presenceRenderer = new PresenceRenderer(main);
    this.scene.scene.add(this.renderer.group);
    this.scene.scene.add(this.previewRenderer.group);
    this.scene.scene.add(this.vanRenderer.group);
    this.scene.scene.add(this.presenceRenderer.group);
    this.toolManager = new ToolManager(this.canvas);

    // UI
    this.topBar = new TopBar(root, {
      mode: this.projectBinding ? 'project' : 'draft',
      onNew: () => this.newProject(),
      onSave: () => this.saveProject(),
      onExport: () => saveProjectFile(this.currentProjectData()),
      onImport: () => void this.importProjectFile(),
      onBack: this.projectBinding ? () => this.goToDashboard() : undefined,
      onRenameProject: this.projectBinding ? (name) => void this.renameProject(name) : undefined,
      onUndo: () => this.undo(),
      onRedo: () => this.redo(),
      onGroup: () => this.groupSelection(),
      onUngroup: () => this.ungroupSelection(),
      onFlip: (axis) => this.flipSelection(axis),
      onView: (v) => { this.camera.setView(v); this.requestUpdate(); },
      onZoomExtents: () => this.zoomExtents(),
      onHelp: () => this.overlay.toggle(),
      onShare: () => this.shareOverlay.toggle(),
      onStopFollow: () => this.stopFollow('manual'),
      onTogglePanels: () => this.togglePanels()
    });
    if (this.projectBinding) this.topBar.setProjectName(this.projectBinding.name);
    // DOM-Reihenfolge = visuelle Reihenfolge: Kopfzeile oben, Arbeitsfläche Mitte.
    // (TopBar hängt sich selbst an den Root — main darf erst danach folgen, sonst
    // rutscht die „Kopfzeile“ visuell unter den Canvas.)
    root.appendChild(main);
    this.overlay = new ShortcutOverlay(root);
    this.shareOverlay = new ShareOverlay(root, {
      onStart: (name) => {
        const roomId = this.joinRoomId ?? newRoomId();
        const asHost = this.joinRoomId === null;
        this.joinRoomId = null;
        this.startCollab(roomId, name, asHost);
      },
      onRename: (name) => {
        localStorage.setItem('campsketch.user.name', name);
        this.collab?.rename(name);
      },
      onLeave: () => this.leaveCollab(),
      onFollowToggle: (clientId) => this.toggleFollow(clientId),
      onFollowInvite: (clientId) => this.inviteFollow(clientId),
      onInviteAll: () => this.inviteAll()
    });
    // Online-Teilnehmer direkt in der Kopfzeile — Antippen folgt der Person
    this.participantsBar = new ParticipantsBar(this.topBar.participantsRoot, {
      onFollowToggle: (clientId) => this.toggleFollow(clientId),
      onInvite: (clientId) => this.inviteFollow(clientId),
      onInviteAll: () => this.inviteAll()
    });
    this.followInviteOverlay = new FollowInviteOverlay(root, {
      onAccept: (inviter) => {
        this.toggleFollow(inviter.clientId);
        this.collab?.respondFollowInvite(inviter.clientId, true);
      },
      onDecline: (inviter) => {
        if (!inviter) return;
        this.collab?.respondFollowInvite(inviter.clientId, false);
        this.statusBar.setStatus(`Einladung von „${inviter.name}“ abgelehnt`);
      }
    });

    this.statusBar = new StatusBar(root, this.unitsValue, (u) => {
      this.unitsValue = u;
      this.needsSave = true;
    });
    this.ui = {
      setStatus: (hint) => this.statusBar.setStatus(hint)
    };

    // Werkzeuge registrieren (vor den Panels — das Möbel-Panel schaltet das Platzier-Werkzeug)
    this.placeTool = new PlaceTool(this);
    const tools = [
      new SelectTool(this),
      new LineTool(this),
      new RectangleTool(this),
      new CircleTool(this),
      new ArcTool(this),
      new OffsetTool(this),
      new PushPullTool(this),
      new MoveTool(this),
      new RotateTool(this),
      new ScaleTool(this),
      new EraserTool(this),
      new TapeTool(this),
      new PaintTool(this),
      this.placeTool,
      new OrbitTool(this),
      new PanTool(this),
      new ZoomTool(this)
    ];
    for (const t of tools) this.toolManager.register(t);

    const panelsRoot = document.createElement('aside');
    panelsRoot.className = 'panels-root';
    main.appendChild(panelsRoot);
    this.panelsRootEl = panelsRoot;
    // Seitenleiste: gespeicherte Wahl gilt, sonst mobil (≤900px) standardmäßig eingeklappt
    const storedPanels = localStorage.getItem(PANELS_KEY);
    this.panelsHidden = storedPanels !== null ? storedPanels === '1' : window.matchMedia(PANELS_MOBILE_QUERY).matches;
    this.applyPanelsHidden();
    this.panels = new Panels(panelsRoot, VAN_TEMPLATES, this.van, {
      onVanChange: (s) => {
        this.van = s;
        this.vanRenderer.update(s);
        this.needsSave = true;
        this.fitToVan();
      },
      onColorPick: (c) => { this.currentColorValue = c; },
      onPlaceFurniture: (tpl) => {
        this.placeTool.setTemplate(tpl);
        this.switchTool('place');
      },
      onSaveSelectionAsFurniture: (name) => this.saveSelectionAsFurniture(name),
      onDeleteCustomFurniture: (id) => {
        this.panels.renderCustomFurniture(removeCustomFurniture(id));
        this.needsSave = true;
      },
      onToggleLayerVisible: (target) => this.toggleLayerVisible(target),
      onSelectLayer: (target) => this.selectLayer(target),
      onShowAllLayers: () => this.showAllLayers()
    }, this.currentColorValue);
    this.panels.renderCustomFurniture(listCustomFurniture());
    this.panels.refreshLayers(this.computeLayers());
    this.entityInfo = new EntityInfoPanel(this.panels.el, {
      onEdgeLength: (m) => this.applyEdgeLength(m),
      onDims: (d) => this.applyDims(d),
      onPosition: (p) => this.applyPosition(p),
      onGroupName: (gid, name) => {
        const g = this.model.groups.get(gid);
        if (!g || (g.name ?? '') === name) return;
        this.beginEdit();
        g.name = name || undefined;
        this.requestUpdate();
      },
      onLayerOpacityBegin: () => this.beginEdit(),
      onLayerOpacity: (groupId, opacity) => {
        if (groupId === null) this.model.looseOpacity = opacity;
        else {
          const g = this.model.groups.get(groupId);
          if (g) g.opacity = opacity;
        }
        this.requestUpdate();
      }
    }, () => this.unitsValue);

    // Werkzeugleiste (nur die zeichnenden Werkzeuge — „Möbel stellen“ startet aus dem Panel)

    const toolbarIds = [
      'select', 'line', 'rectangle', 'circle', 'arc', 'offset',
      'pushpull', 'move', 'rotate', 'scale', 'eraser', 'tape', 'paint',
      'orbit', 'pan', 'zoom'
    ];
    const toolbarRoot = document.createElement('div');
    toolbarRoot.className = 'toolbar-root';
    main.appendChild(toolbarRoot);
    const toolbarDefs = toolbarIds.map((id) => {
      const t = this.toolManager.get(id)!;
      return { id, label: t.label, shortcut: t.shortcut, icon: id };
    });
    const toolbar = new Toolbar(toolbarRoot, toolbarDefs, (id) => this.switchTool(id));
    // Mobil: dieselben Werkzeuge als horizontal scrollbare Leiste am unteren
    // Rand (Daumenbedienung); auf dem Desktop per CSS ausgeblendet.
    const toolbarBottomRoot = document.createElement('div');
    toolbarBottomRoot.className = 'toolbar-bottom-root';
    const toolbarBottom = new Toolbar(toolbarBottomRoot, toolbarDefs, (id) => this.switchTool(id));
    root.appendChild(toolbarBottomRoot);
    this.toolManager.onSwitch((tool) => {
      this.vcbBufferValue = '';
      toolbar.setActive(tool.id);
      toolbarBottom.setActive(tool.id);
      this.statusBar.setDefaultHint(tool.hint);
      this.statusBar.setStatus(null);
      this.panels.setActiveFurniture(
        tool.id === 'place' ? this.placeTool.currentTemplate()?.id ?? null : null
      );
    });

    // Mobil: Abdunkelung hinter der Seitenleiste (Drawer) — Antippen schließt sie
    const panelsBackdrop = document.createElement('div');
    panelsBackdrop.className = 'drawer-backdrop';
    panelsBackdrop.onclick = () => {
      if (!this.panelsHidden) this.togglePanels();
    };
    root.appendChild(panelsBackdrop);
    this.panelsBackdropEl = panelsBackdrop;
    // Nachtrag: der Zustand wurde vor dem Backdrop bereits angewendet
    this.applyPanelsHidden();
    // Orientierungswechsel/Fenstergröße: Drawer-Zustand neu bewerten
    window.matchMedia(PANELS_MOBILE_QUERY).addEventListener('change', () => this.applyPanelsHidden());

    // Touch-Navigation: Ein-Finger-Werkzeug/Orbit, Zwei-Finger Pan+Pinch, Long-Press-Menü
    this.touchNav = new TouchNavigator({
      camera: this.camera,
      activeToolId: () => this.toolManager.active?.id ?? 'select',
      forwardToolDown: (e) => {
        this.vcbBufferValue = '';
        this.leftDown = true;
        this.toolManager.active?.pointerDown(this.toToolPointer(e));
      },
      forwardToolMove: (e) => {
        this.toolManager.active?.pointerMove({ ...this.toToolPointer(e), button: 0 });
      },
      forwardToolUp: (e) => {
        this.leftDown = false;
        this.toolManager.active?.pointerUp({ ...this.toToolPointer(e), button: 0 });
      },
      abortTool: () => {
        this.leftDown = false;
        this.toolManager.active?.cancel();
      },
      openContextMenu: (clientX, clientY) => {
        this.onContextMenu({ clientX, clientY, preventDefault: () => {} } as unknown as MouseEvent);
      },
      ndcAt: (clientX, clientY) => {
        const rect = this.canvas.getBoundingClientRect();
        return {
          x: ((clientX - rect.left) / rect.width) * 2 - 1,
          y: -((clientY - rect.top) / rect.height) * 2 + 1
        };
      },
      requestUpdate: () => this.requestUpdate()
    });

    // Events (alle über einen AbortController — destroy() räumt sie mit einem Rutsch ab)
    const signal = this.ac.signal;
    this.canvas.addEventListener('pointerdown', this.onPointerDown, { signal });
    this.canvas.addEventListener('pointermove', this.onPointerMove, { signal });
    this.canvas.addEventListener('pointerup', this.onPointerUp, { signal });
    this.canvas.addEventListener('pointerleave', () => this.collab?.sendCursor(null), { signal });
    this.canvas.addEventListener('pointercancel', this.onPointerCancel, { signal });
    this.canvas.addEventListener('lostpointercapture', (e) => {
      // Capture verloren ohne Loslassen (z. B. Fensterwechsel) → wie Abbruch behandeln
      if (this.middleDrag || this.leftDown) {
        if ((e.buttons & 5) === 0) return; // normales Loslassen folgt/ist erfolgt
        this.onPointerCancel();
      }
    }, { signal });
    this.canvas.addEventListener('dblclick', this.onDoubleClick, { signal });
    this.canvas.addEventListener('wheel', this.onWheel, { passive: false, signal });
    this.canvas.addEventListener('contextmenu', this.onContextMenu, { signal });
    window.addEventListener('keydown', this.onKeyDown, { signal });
    // iOS Safari: Seiten-Zoom/Pinch außerhalb der eigenen Pointer-Logik unterbinden
    const stopBrowserGesture = (e: Event): void => e.preventDefault();
    document.addEventListener('gesturestart', stopBrowserGesture, { signal });
    document.addEventListener('gesturechange', stopBrowserGesture, { signal });
    // Knöpfe nehmen keinen Tastaturfokus an (sonst löst Enter/Leertaste sie erneut aus) …
    document.addEventListener('mousedown', this.onDocMouseDown, { signal });
    // … und Auswahllisten geben den Fokus nach der Auswahl zurück (sonst springen Tastenkürzel im Feld)
    document.addEventListener('change', this.onDocChange, { signal });
    window.addEventListener('resize', this.onResize, { signal });
    // Beim Schließen/Neuladen letzten Stand sofort sichern
    window.addEventListener('pagehide', this.onPageHide, { signal });
    // Layout-Änderungen (z. B. Panel ein/aus) ohne Fenster-Resize sauber übernehmen
    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.resizeObserver.observe(this.canvas);

    // Start
    this.vanRenderer.update(this.van);
    this.resize();
    this.camera.setView('iso');
    this.fitToVan();
    if (options.project && options.data) {
      // Server-Projekt: Inhalt kommt aus der Datenbank — lokaler Autosave bleibt außen vor
      this.applyProject(options.data);
      document.title = `${options.project.name} — CampSketch`;
      // Erstmalig hochladen, damit auch ohne Änderung ein Vorschaubild entsteht
      this.serverDirty = true;
      this.scheduleServerSave(true);
    } else {
      // Anonymer Modus (/join): letzten lokalen Stand wiederherstellen
      this.restoreAutosave();
    }
    this.switchTool('select');

    // Share-Link geöffnet → automatisch beitreten (ohne gespeicherten Namen erst fragen)
    const roomParam = new URLSearchParams(location.search).get('room');
    if (roomParam && /^[A-Za-z0-9_-]{4,64}$/.test(roomParam)) {
      const savedName = localStorage.getItem('campsketch.user.name');
      if (savedName) this.startCollab(roomParam, savedName, false);
      else {
        this.joinRoomId = roomParam;
        this.shareOverlay.showJoin();
      }
    }

    // Render-Loop: rAF wenn sichtbar, plus Timer-Sicherheitsnetz (rAF steht in
    // einbettenen/verdeckten Viewports manchmal still)
    const loop = (): void => {
      if (this.destroyed) return;
      this.tick();
      this.rafHandle = requestAnimationFrame(loop);
    };
    this.rafHandle = requestAnimationFrame(loop);
    this.tickInterval = window.setInterval(() => this.tick(), 250);
    this.tick();
  }

  // ---------- Seitenleiste ----------

  /** Seitenleiste ein-/ausklappen (Knopf ☰ in der Kopfzeile); Wahl wird gespeichert. */
  private togglePanels(): void {
    this.panelsHidden = !this.panelsHidden;
    try {
      localStorage.setItem(PANELS_KEY, this.panelsHidden ? '1' : '0');
    } catch {
      // Speicher verweigert (Privatmodus) — Umschalten wirkt trotzdem
    }
    this.applyPanelsHidden();
  }

  /** Sichtbarkeits-Klasse setzen, Knopf synchron halten, Canvas-Maße sofort übernehmen. */
  private applyPanelsHidden(): void {
    this.panelsRootEl?.classList.toggle('is-hidden', this.panelsHidden);
    this.topBar.setPanelsHidden(this.panelsHidden);
    // Abdunkelung nur mobil, solange der Drawer offen ist
    const mobile = window.matchMedia(PANELS_MOBILE_QUERY).matches;
    this.panelsBackdropEl?.classList.toggle('visible', !this.panelsHidden && mobile);
    // Der ResizeObserver reagiert erst asynchron — für einen sauberen ersten Frame direkt messen
    if (this.canvas.clientWidth > 0) this.resize();
  }

  private tickScheduled = false;

  /** Ein Frame Anzeige-Aktualisierung (Modell, UI, Szene). */
  private tick(): void {
    if (this.destroyed) return;
    try {
      if (this.modelDirty) {
        this.model.pruneEmptyGroups();
        // Offene Gruppe gelöscht (Undo, Radieren …) → zur nächsten existierenden Ebene
        while (this.model.editContext !== undefined && !this.model.groups.has(this.model.editContext)) {
          this.model.editContext = undefined;
        }
        this.renderer.update();
        this.modelDirty = false;
        this.panels.setInfo(this.model.edges.size, this.model.faces.size, this.model.guides.size);
        this.panels.refreshLayers(this.computeLayers());
      }
      this.entityInfo.render(this.computeSelectionInfo());
      this.topBar.setUndoRedo(this.history.canUndo, this.history.canRedo);
      this.refreshVcb();
      this.maybeAutosave();
      this.collab?.maybeSync();
      this.stepFollow();
      this.syncOwnView();
      this.presenceRenderer.render(this.camera.camera, this.canvas);
      this.scene.render(this.camera.camera);
    } catch (err) {
      // Ein Renderfehler darf den Loop nicht abwürfen — Fehler sichtbar loggen
      console.error('CampSketch Renderfehler:', err);
    }
  }

  // ---------- AppContext ----------

  /**
   * Editor-Instanz sauber abbauen (Screen-Wechsel durch den Router): Loops stoppen,
   * Listener abmelden, Kollaboration verlassen, GPU-Ressourcen freigeben.
   */
  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    cancelAnimationFrame(this.rafHandle);
    window.clearInterval(this.tickInterval);
    if (this.serverSaveTimer !== null) {
      window.clearTimeout(this.serverSaveTimer);
      this.serverSaveTimer = null;
    }
    this.ac.abort();
    this.resizeObserver.disconnect();
    this.closeContextMenu();
    this.leaveCollab();
    if (this.serverDirty) void this.flushServerSave(); // best effort beim Verlassen
    this.disposeGroup(this.renderer.group);
    this.disposeGroup(this.previewRenderer.group);
    this.disposeGroup(this.vanRenderer.group);
    this.disposeGroup(this.presenceRenderer.group);
    this.scene.renderer.dispose();
    window.campsketch = undefined;
  }

  private disposeGroup(group: THREE.Object3D): void {
    group.traverse((obj) => {
      const o = obj as THREE.Object3D & { geometry?: THREE.BufferGeometry; material?: THREE.Material | THREE.Material[] };
      o.geometry?.dispose();
      if (Array.isArray(o.material)) o.material.forEach((m) => m.dispose());
      else o.material?.dispose();
    });
  }

  private onDocMouseDown = (e: MouseEvent): void => {
    if ((e.target as HTMLElement | null)?.closest('button')) e.preventDefault();
  };

  private onDocChange = (e: Event): void => {
    const t = e.target as HTMLElement | null;
    if (t?.tagName === 'SELECT') (t as HTMLSelectElement).blur();
  };

  private onPageHide = (): void => {
    this.maybeAutosave(true);
    if (this.projectBinding && this.serverDirty) void this.flushServerSave();
  };

  requestUpdate(): void {
    this.modelDirty = true;
    // Autosave prüft beim nächsten Intervall, ob sich der Inhalt wirklich geändert hat
    this.needsSave = true;
    // Kollaboration: potenzielle Änderung beim nächsten Flush diffen
    this.collab?.markDirty();
    // Sofortiges Nachziehen, falls rAF pausiert (verdeckter Viewport)
    if (!this.tickScheduled) {
      this.tickScheduled = true;
      setTimeout(() => {
        this.tickScheduled = false;
        this.tick();
      }, 0);
    }
  }

  switchTool(id: string): void {
    this.toolManager.switchTo(id);
  }

  beginEdit(): void {
    this.history.push(this.model);
    this.needsSave = true;
  }

  units(): Unit {
    return this.unitsValue;
  }

  setUnits(u: Unit): void {
    this.unitsValue = u;
  }

  currentColor(): string {
    return this.currentColorValue;
  }

  setCurrentColor(c: string): void {
    this.currentColorValue = c;
  }

  setViewportCursor(cursor: string | null): void {
    this.canvas.style.cursor = cursor ?? this.toolManager.active?.cursor ?? 'default';
  }

  vcbActive(): boolean {
    return this.vcbBufferValue.length > 0;
  }

  vcbBuffer(): string {
    return this.vcbBufferValue;
  }

  setVcbBuffer(s: string): void {
    this.vcbBufferValue = s;
  }

  // ---------- Pointer ----------

  private toToolPointer(e: PointerEvent | MouseEvent): ToolPointer {
    const rect = this.canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const ndcX = (x / rect.width) * 2 - 1;
    const ndcY = -(y / rect.height) * 2 + 1;
    return {
      x, y, ndcX, ndcY,
      ray: this.camera.rayFromNdc(ndcX, ndcY),
      button: 'button' in e ? e.button : 0,
      shift: e.shiftKey,
      ctrl: e.ctrlKey || e.metaKey,
      alt: e.altKey
    };
  }

  private onPointerDown = (e: PointerEvent): void => {
    try {
      this.canvas.setPointerCapture(e.pointerId);
    } catch {
      // Synthetische Events haben keinen aktiven Pointer — Ziehen funktioniert trotzdem
    }
    if (e.pointerType === 'touch') {
      // Touch läuft über den TouchNavigator (Werkzeug bzw. Kamera-Gesten)
      this.touchNav.pointerDown(e);
      return;
    }
    if (e.button === 1) {
      // Mittlere Maustaste: Umlauf um den Punkt unter dem Cursor, mit Umschalt Schwenken (wie SketchUp)
      const p = this.toToolPointer(e);
      const pan = e.shiftKey;
      this.middleDrag = { x: e.clientX, y: e.clientY, startX: e.clientX, startY: e.clientY, pan };
      if (pan) this.camera.beginPan(p.ndcX, p.ndcY);
      else this.camera.beginOrbit(p.ndcX, p.ndcY);
      this.setViewportCursor(pan ? 'grabbing' : 'move');
      e.preventDefault();
      return;
    }
    if (e.button !== 0) return;
    // macOS: Strg+Klick ist Rechtsklick → nur Kontextmenü, kein Werkzeug-Klick
    if (this.isMac && e.ctrlKey && !e.metaKey) return;
    // Angefangene Maßkasten-Eingabe verfällt beim Klick (der Mauswert gilt, nicht der alte Text)
    this.vcbBufferValue = '';
    this.leftDown = true;
    const p = this.toToolPointer(e);
    this.toolManager.active?.pointerDown(p);
  };

  /** Beendet einen Mittelklick-Umlauf/-Schwenk (auch wenn das Loslassen verloren ging). */
  private endMiddleDrag(): void {
    this.middleDrag = null;
    this.camera.endOrbit();
    this.camera.endPan();
    this.setViewportCursor(null);
  }

  /** Zeiger abgebrochen (Fensterwechsel, Capture verloren): alle Ziehvorgänge sauber beenden. */
  private onPointerCancel = (e?: PointerEvent): void => {
    if (e && e.pointerType === 'touch') {
      this.touchNav.pointerCancel(e);
      return;
    }
    if (this.middleDrag) this.endMiddleDrag();
    if (this.leftDown) {
      this.leftDown = false;
      this.toolManager.active?.abort();
    }
  };

  private onPointerMove = (e: PointerEvent): void => {
    if (e.pointerType === 'touch') {
      this.touchNav.pointerMove(e);
      // Presence-Cursor mitsenden, solange der Finger ins Werkzeug zeichnet
      if (this.touchNav.isForwardingTool) this.trackPresenceCursor(e);
      return;
    }
    // Loslassen verpasst (z. B. Tasten-Akkord Mitte+Links)? Zustand an echte Tasten angleichen
    if (this.middleDrag && (e.buttons & 4) === 0) this.endMiddleDrag();
    if (this.leftDown && (e.buttons & 1) === 0) {
      this.leftDown = false;
      this.toolManager.active?.pointerUp({ ...this.toToolPointer(e), button: 0 });
    }
    if (this.middleDrag) {
      const dx = e.clientX - this.middleDrag.x;
      const dy = e.clientY - this.middleDrag.y;
      this.middleDrag.x = e.clientX;
      this.middleDrag.y = e.clientY;
      // Umschalt während des Ziehens umschalten → zwischen Umlauf und Schwenken wechseln
      if (e.shiftKey !== this.middleDrag.pan) {
        const p = this.toToolPointer(e);
        this.middleDrag.pan = e.shiftKey;
        if (e.shiftKey) this.camera.beginPan(p.ndcX, p.ndcY);
        else this.camera.beginOrbit(p.ndcX, p.ndcY);
        this.setViewportCursor(e.shiftKey ? 'grabbing' : 'move');
      }
      if (this.middleDrag.pan) this.camera.pan(dx, dy);
      else this.camera.orbit(dx, dy);
      this.requestUpdate();
      return;
    }
    this.toolManager.active?.pointerMove(this.toToolPointer(e));
    this.trackPresenceCursor(e);
  };

  private onPointerUp = (e: PointerEvent): void => {
    if (e.pointerType === 'touch') {
      this.touchNav.pointerUp(e);
      return;
    }
    if (e.button === 1) {
      const d = this.middleDrag;
      this.endMiddleDrag();
      // Doppelklick mit dem Mausrad: Ansicht auf den Punkt zentrieren
      if (d && Math.hypot(e.clientX - d.startX, e.clientY - d.startY) < 4) {
        const now = performance.now();
        if (now - this.lastMiddleClickAt < 400) {
          const p = this.toToolPointer(e);
          this.camera.centerAt(p.ndcX, p.ndcY);
          this.requestUpdate();
          this.lastMiddleClickAt = 0;
        } else {
          this.lastMiddleClickAt = now;
        }
      }
      return;
    }
    if (e.button !== 0) return;
    if (this.swallowClick) {
      this.swallowClick = false;
      return;
    }
    if (!this.leftDown) return;
    this.leftDown = false;
    this.toolManager.active?.pointerUp(this.toToolPointer(e));
  };

  private onDoubleClick = (e: MouseEvent): void => {
    this.toolManager.active?.doubleClick(this.toToolPointer(e));
  };

  private onWheel = (e: WheelEvent): void => {
    e.preventDefault();
    // Mausrad/Trackpad: stufenlos zum Cursor zoomen; Pinch (ctrlKey) stärker gewichten
    const px = e.deltaMode === 1 ? e.deltaY * 16 : e.deltaMode === 2 ? e.deltaY * 400 : e.deltaY;
    const k = e.ctrlKey ? 0.01 : 0.0015;
    const factor = Math.min(2, Math.max(0.5, Math.exp(px * k)));
    const p = this.toToolPointer(e);
    this.camera.zoomAt(factor, p.ndcX, p.ndcY);
    this.requestUpdate();
  };

  private onResize = (): void => {
    this.resize();
  };

  private resize(): void {
    this.camera.camera.aspect = this.canvas.clientWidth / Math.max(1, this.canvas.clientHeight);
    this.camera.camera.updateProjectionMatrix();
    this.scene.resize();
  }

  // ---------- Tastatur (SketchUp-Shortcuts) ----------

  private onKeyDown = (e: KeyboardEvent): void => {
    const target = e.target as HTMLElement | null;
    const ctrl = e.ctrlKey || e.metaKey;
    const inField = !!target && ['INPUT', 'SELECT', 'TEXTAREA'].includes(target.tagName);
    // Speichern/Öffnen auch aus Eingabefeldern (sonst öffnet der Browser seinen eigenen Dialog)
    if (ctrl && !e.altKey && ['s', 'o'].includes(e.key.toLowerCase())) {
      e.preventDefault();
      if (e.key.toLowerCase() === 's') this.saveProject();
      else void this.openProject();
      return;
    }
    if (inField) {
      // Enter in einem Feld: Wert übernehmen und Fokus zurück an die Arbeitsfläche
      if (e.key === 'Enter' && target!.tagName === 'INPUT') (target as HTMLInputElement).blur();
      return;
    }

    const tool = this.toolManager.active;

    // Teilen-Dialog offen: nur Esc schließt, andere Tasten wirken nicht aufs Modell
    if (this.shareOverlay.isVisible) {
      if (e.key === 'Escape') this.shareOverlay.hide();
      e.preventDefault();
      return;
    }

    // Hilfe offen: Esc/? schließen sie, andere Tasten wirken nicht aufs Modell
    if (this.overlay.isVisible) {
      if (e.key === 'Escape' || e.key === '?') this.overlay.toggle();
      e.preventDefault();
      return;
    }

    if (ctrl) {
      const k = e.key.toLowerCase();
      if (k === 'z' && !e.shiftKey) { this.undo(); e.preventDefault(); return; }
      if (k === 'z' && e.shiftKey) { this.redo(); e.preventDefault(); return; }
      if (k === 'y') { this.redo(); e.preventDefault(); return; }
      if (k === 's') { this.saveProject(); e.preventDefault(); return; }
      if (k === 'o') { void this.openProject(); e.preventDefault(); return; }
      if (k === 'g' && e.shiftKey) { this.ungroupSelection(); e.preventDefault(); return; }
      if (k === 'g') { this.groupSelection(); e.preventDefault(); return; }
      if (k === 'e' && e.shiftKey) { this.zoomExtents(); e.preventDefault(); return; }
      return;
    }

    if (e.key === '?') { this.overlay.toggle(); return; }

    if (e.key === 'Escape') {
      if (this.contextMenuEl) {
        this.closeContextMenu();
      } else if (this.vcbBufferValue) {
        this.vcbBufferValue = '';
      } else {
        tool?.cancel();
        this.switchTool('select');
      }
      return;
    }

    if (e.key === 'Enter') {
      // Kein Standardverhalten: ein fokussierter Knopf würde sonst erneut ausgelöst
      e.preventDefault();
      if (this.vcbBufferValue && tool?.wantsVcb()) {
        tool.applyVcb(this.vcbBufferValue);
        this.vcbBufferValue = '';
      }
      return;
    }

    if (e.key === 'Backspace' || e.key === 'Delete') {
      if (this.vcbBufferValue) {
        this.vcbBufferValue = this.vcbBufferValue.slice(0, -1);
      } else {
        this.deleteSelection();
      }
      e.preventDefault();
      return;
    }

    if (e.key.startsWith('Arrow')) {
      tool?.keyDown(e);
      e.preventDefault();
      return;
    }

    // Maßkasten: nach der ersten Ziffer gehen alle Zeichen (auch Einheiten wie „cm“, „m“,
    // Leerzeichen, °, ") in die Eingabe statt an Tastenkürzel
    if (e.key.length === 1 && tool?.wantsVcb() && !e.altKey) {
      const starts = /[0-9.,\-]/.test(e.key);
      if (this.vcbBufferValue || starts) {
        this.vcbBufferValue += e.key;
        e.preventDefault();
        return;
      }
    }

    if (e.key === ' ') {
      this.switchTool('select');
      e.preventDefault();
      return;
    }

    // Spiegeln: Umschalt+H / Umschalt+J (horizontal rot/grün), Umschalt+V (vertikal) — nicht mitten in einer Aktion
    if (e.shiftKey && !e.altKey && !this.vcbBufferValue && !tool?.wantsVcb()) {
      const flip = ({ H: 0, J: 1, V: 2 } as Record<string, 0 | 1 | 2>)[e.key.toUpperCase()];
      if (flip !== undefined) {
        this.flipSelection(flip);
        e.preventDefault();
        return;
      }
    }

    if (e.key.length === 1) {
      const ch = e.key;
      // Beim Drehen gehören Ziffern dem Winkel, nicht den Ansichten (sonst „45“ kippt die Kamera)
      if (VIEW_KEYS[ch] && this.toolManager.active?.id !== 'rotate') {
        this.camera.setView(VIEW_KEYS[ch]!);
        return;
      }
      const toolId = TOOL_SHORTCUTS[ch.toLowerCase()];
      if (toolId) this.switchTool(toolId);
    }
  };

  // ---------- Befehle ----------

  private deleteSelection(): void {
    if (this.selection.isEmpty) return;
    this.beginEdit();
    eraseEdges(this.model, this.selection.edges);
    for (const id of this.selection.faces) this.model.deleteFace(id);
    this.selection.clear();
    this.requestUpdate();
  }

  private groupSelection(): void {
    if (this.selection.isEmpty) return;
    this.beginEdit();
    const gid = this.model.groupEntities(this.selection.edges, this.selection.faces);
    // Wie SketchUp: danach ist die neue Gruppe als Ganzes gewählt
    const { edges, faces } = this.model.entitiesInGroup(gid);
    this.selection.set(edges, faces);
    this.requestUpdate();
  }

  /** Auflösen (SketchUp „Explode“): gewählte Gruppen eine Ebene nach oben auflösen, Inhalt bleibt gewählt. */
  private ungroupSelection(): void {
    const groups = this.model.groupsAt(this.selection.edges, this.selection.faces);
    if (groups.size === 0) return;
    this.beginEdit();
    const edges = new Set<number>();
    const faces = new Set<number>();
    // Nicht gewählte lose Elemente bleiben ungewählt, lose gewählte bleiben gewählt
    for (const id of this.selection.edges) if (this.model.edges.get(id)?.group === this.model.editContext) edges.add(id);
    for (const id of this.selection.faces) if (this.model.faces.get(id)?.group === this.model.editContext) faces.add(id);
    for (const gid of groups) {
      const r = this.model.explodeGroup(gid);
      r.edges.forEach((id) => edges.add(id));
      r.faces.forEach((id) => faces.add(id));
    }
    this.selection.set(
      [...edges].filter((id) => this.model.edges.has(id)),
      [...faces].filter((id) => this.model.faces.has(id))
    );
    this.requestUpdate();
  }

  /**
   * Spiegeln entlang rot (0), grün (1) oder blau (2). Ist genau eine Gruppe gewählt,
   * gelten ihre eigenen Achsen (wie SketchUp), sonst die Weltachsen.
   */
  private flipSelection(axis: 0 | 1 | 2): void {
    if (this.selection.isEmpty) {
      this.statusBar.setStatus('Erst etwas auswählen, dann spiegeln');
      return;
    }
    const single = this.selectedSingleGroup();
    const dir = single ? this.model.groupAxes(single.id)[axis] : [vec(1, 0, 0), vec(0, 1, 0), vec(0, 0, 1)][axis]!;
    this.beginEdit();
    flipEntities(this.model, this.selection.edges, this.selection.faces, dir);
    this.requestUpdate();
  }

  /** Gruppe öffnen (Bearbeitungskontext), Auswahl aufheben. */
  private editGroup(gid: number): void {
    // Ausgeblendete Gruppe beim Bearbeiten zeigen (sonst editiert man im Blindflug)
    const g = this.model.groups.get(gid);
    if (g && g.visible === false) g.visible = true;
    this.selection.clear();
    this.setActiveGroupId(gid);
    this.statusBar.setStatus('Gruppe bearbeiten · Klick außerhalb oder Esc schließt die Gruppe');
  }

  /** Offene Gruppe schließen (eine Ebene nach oben) und als Ganzes wählen. */
  closeGroup(): void {
    const gid = this.model.editContext;
    if (gid === undefined) return;
    this.setActiveGroupId(this.model.parentOf(gid) ?? null);
    const { edges, faces } = this.model.entitiesInGroup(gid);
    this.selection.set(edges, faces);
    this.statusBar.setStatus(null);
  }

  private undoRedoContext(): void {
    this.selection.clear();
    // In der offenen Gruppe bleiben, solange sie existiert (wie SketchUp)
    const ctx = this.model.editContext;
    if (ctx !== undefined && !this.model.groups.has(ctx)) this.model.editContext = undefined;
    this.requestUpdate();
  }

  private undo(): void {
    // Laufende Aktion zuerst abbrechen, sonst arbeitet sie am wiederhergestellten Modell weiter
    this.toolManager.active?.abort();
    this.vcbBufferValue = '';
    if (this.history.undo(this.model)) this.undoRedoContext();
  }

  private redo(): void {
    this.toolManager.active?.abort();
    this.vcbBufferValue = '';
    if (this.history.redo(this.model)) this.undoRedoContext();
  }

  // ---------- Kontextmenü (Rechtsklick, wie SketchUp) ----------

  private contextMenuEl: HTMLDivElement | null = null;

  private closeContextMenu = (): void => {
    this.contextMenuEl?.remove();
    this.contextMenuEl = null;
    window.removeEventListener('pointerdown', this.onOutsideMenu, true);
  };

  private onOutsideMenu = (e: PointerEvent): void => {
    if (!this.contextMenuEl || this.contextMenuEl.contains(e.target as Node)) return;
    this.closeContextMenu();
    // Der Klick schließt nur das Menü — er soll nicht zusätzlich im Modell wirken
    if (e.target === this.canvas && e.button === 0) {
      e.stopPropagation();
      e.preventDefault();
      this.swallowClick = true;
    }
  };

  private onContextMenu = (e: MouseEvent): void => {
    e.preventDefault();
    this.closeContextMenu();
    const p = this.toToolPointer(e);
    const { edgeId, faceId } = pickAt(this, p);
    // Rechtsklick auf nicht gewähltes Objekt des Kontexts wählt es zuerst (wie SketchUp)
    if (edgeId !== null || faceId !== null) {
      const inSel = edgeId !== null ? this.selection.edges.has(edgeId) : this.selection.faces.has(faceId!);
      if (!inSel) {
        const target = this.model.expandToGroups(edgeId !== null ? [edgeId] : [], faceId !== null ? [faceId] : []);
        if (target.edges.size + target.faces.size > 0) this.selection.set(target.edges, target.faces);
      }
    }
    this.requestUpdate();

    const items: { label: string; hint?: string; action: () => void }[] = [];
    const groups = this.model.groupsAt(this.selection.edges, this.selection.faces);
    const hasLoose = [...this.selection.edges].some((id) => this.model.edges.get(id)?.group === this.model.editContext) ||
      [...this.selection.faces].some((id) => this.model.faces.get(id)?.group === this.model.editContext);
    if (groups.size === 1 && !hasLoose) {
      const gid = [...groups][0]!;
      items.push({ label: 'Gruppe bearbeiten', action: () => this.editGroup(gid) });
    }
    if (groups.size > 0) items.push({ label: 'Auflösen', hint: 'Strg+Umschalt+G', action: () => this.ungroupSelection() });
    if (!this.selection.isEmpty) {
      items.push({ label: 'Ausblenden', action: () => this.hideSelection() });
      items.push({ label: 'Horizontal spiegeln (rot)', hint: 'Umschalt+H', action: () => this.flipSelection(0) });
      items.push({ label: 'Horizontal spiegeln (grün)', hint: 'Umschalt+J', action: () => this.flipSelection(1) });
      items.push({ label: 'Vertikal spiegeln (blau)', hint: 'Umschalt+V', action: () => this.flipSelection(2) });
      items.push({ label: 'Gruppieren', hint: 'Strg+G', action: () => this.groupSelection() });
      items.push({ label: 'Löschen', hint: 'Entf', action: () => this.deleteSelection() });
    }
    if (this.model.editContext !== undefined) items.push({ label: 'Gruppe schließen', hint: 'Esc', action: () => this.closeGroup() });
    items.push({ label: 'Alles zeigen', hint: 'Strg+Umschalt+E', action: () => this.zoomExtents() });

    const menu = document.createElement('div');
    menu.className = 'context-menu';
    for (const it of items) {
      const b = document.createElement('button');
      b.type = 'button';
      const label = document.createElement('span');
      label.textContent = it.label;
      b.appendChild(label);
      if (it.hint) {
        const kbd = document.createElement('kbd');
        kbd.textContent = it.hint;
        b.appendChild(kbd);
      }
      b.onclick = () => {
        this.closeContextMenu();
        it.action();
        this.requestUpdate();
      };
      menu.appendChild(b);
    }
    document.body.appendChild(menu);
    const r = menu.getBoundingClientRect();
    menu.style.left = `${Math.min(e.clientX, window.innerWidth - r.width - 4)}px`;
    menu.style.top = `${Math.min(e.clientY, window.innerHeight - r.height - 4)}px`;
    this.contextMenuEl = menu;
    window.addEventListener('pointerdown', this.onOutsideMenu, true);
  };

  private zoomExtents(): void {
    const pts = this.renderer.allPoints();
    if (pts.length === 0) this.fitToVan();
    else this.camera.zoomExtents(pts);
  }

  private fitToVan(): void {
    const { length, width, height } = this.van;
    this.camera.zoomExtents([
      { x: -length / 2, y: -width / 2, z: 0 },
      { x: length / 2, y: width / 2, z: height }
    ]);
  }

  /** Vollständiges ProjectData aus dem aktuellen Stand (Datei-Export und Server-Save). */
  private currentProjectData(): ProjectData {
    return {
      version: 1,
      model: this.model.toJSON(),
      van: this.van,
      units: this.unitsValue,
      furniture: listCustomFurniture()
    };
  }

  private saveProject(): void {
    if (this.projectBinding) {
      // In die Projekt-Datenbank speichern (Strg+S erzwingt sofortiges Hochladen)
      this.serverDirty = true;
      this.scheduleServerSave(true);
      return;
    }
    // Entwurfsmodus: Speichern erzeugt erst das Konto (falls nötig), dann das Projekt
    void this.gateSaveToAccount();
  }

  private async openProject(): Promise<void> {
    if (this.projectBinding) {
      location.assign('/'); // Öffnen = Projektliste
      return;
    }
    await this.importProjectFile();
  }

  /**
   * .campsketch.json öffnen: im Projekt-Modus als neues Server-Projekt angelegen,
   * im Entwurfsmodus den lokalen Entwurf ersetzen.
   */
  private async importProjectFile(): Promise<void> {
    try {
      const project = await openProjectFile();
      if (!this.projectBinding) {
        this.applyProject(project);
        return;
      }
      await this.flushServerSave(); // aktuelles Projekt sichern, bevor wir es verlassen
      const { project: created } = await api.createProject('Importiertes Projekt', project);
      location.assign(`/p/${created.id}`);
    } catch (err) {
      if (err instanceof ApiError && err.status === 402) {
        showUpgradeNotice(err.message);
        return;
      }
      alert('Datei konnte nicht importiert werden.');
      console.error(err);
    }
  }

  /** Entwurf sichern: eingeloggt direkt, sonst erst Konto anlegen (E-Mail + Passwort). */
  private async gateSaveToAccount(): Promise<void> {
    if (this.authGateOpen) return;
    let user: SessionUser | null;
    try {
      user = await api.me();
    } catch {
      this.statusBar.setStatus('Speichern nicht möglich — Server nicht erreichbar');
      return;
    }
    if (user) return void this.saveDraftAsProject();
    this.maybeAutosave(true); // Entwurf lokal sichern, bevor der Dialog erscheint
    this.authGateOpen = true;
    new AuthScreen(document.body, {
      asOverlay: true,
      startMode: 'register',
      message: 'Speichere deinen Entwurf als Projekt in der Datenbank — 3 Projekte sind gratis.',
      onCancel: () => {
        this.authGateOpen = false;
      },
      onSuccess: () => {
        this.authGateOpen = false;
        void this.saveDraftAsProject();
      }
    });
  }

  private authGateOpen = false;

  /** Aktuellen Entwurf als Server-Projekt anlegen und dorthin wechseln. */
  private async saveDraftAsProject(): Promise<void> {
    try {
      this.maybeAutosave(true);
      const { project: created } = await api.createProject('Unbenanntes Projekt', this.currentProjectData(), this.captureThumbnail());
      location.assign(`/p/${created.id}`);
    } catch (err) {
      if (err instanceof ApiError && err.status === 402) {
        showUpgradeNotice(err.message);
        return;
      }
      alert('Projekt konnte nicht gespeichert werden.');
      console.error(err);
    }
  }

  private newProject(): void {
    if (this.projectBinding) {
      // Server-Autosave hat den Stand längst gesichert — einfach neu anlegen
      void (async () => {
        await this.flushServerSave();
        const { project } = await api.createProject('Unbenanntes Projekt');
        location.assign(`/p/${project.id}`);
      })().catch(() => this.statusBar.setStatus('Neues Projekt konnte nicht angelegt werden'));
      return;
    }
    if (this.model.edges.size > 0 && !confirm('Neues Modell anlegen? Ungespeicherte Änderungen gehen verloren.')) return;
    this.toolManager.active?.abort();
    this.vcbBufferValue = '';
    this.model = new Model();
    this.rebindModel();
    this.history.clear();
    this.selection.clear();
    this.needsSave = true;
    this.requestUpdate();
    this.zoomExtents();
  }

  private goToDashboard(): void {
    void this.flushServerSave().finally(() => location.assign('/'));
  }

  private async renameProject(name: string): Promise<void> {
    if (!this.projectBinding) return;
    try {
      await api.saveProject(this.projectBinding.id, { name });
      this.projectBinding.name = name;
      document.title = `${name} — CampSketch`;
      this.topBar.setProjectName(name);
      this.statusBar.setStatus('Projekt umbenannt');
    } catch (err) {
      console.error(err);
      this.statusBar.setStatus('Umbenennen fehlgeschlagen');
    }
  }

  /** Gemeinsame Übernahme eines Modellstands (Datei öffnen, Raum beitreten). */
  private applyModelState(model: Model, van: VanState, units: Unit): void {
    // Erst alles aufbauen (kann werfen), dann den App-Zustand austauschen
    this.toolManager.active?.abort();
    this.vcbBufferValue = '';
    this.model = model;
    this.rebindModel();
    this.history.clear();
    this.selection.clear();
    this.unitsValue = units;
    this.statusBar.setUnits(units);
    this.van = van;
    this.panels.syncInputs(van);
    this.vanRenderer.update(van);
    this.needsSave = true;
    this.requestUpdate();
  }

  private applyProject(project: ProjectData): void {
    const model = Model.fromJSON(project.model);
    this.applyModelState(model, project.van, project.units);
    this.panels.renderCustomFurniture(mergeCustomFurniture(project.furniture ?? []));
    if (model.vertices.size > 0) this.zoomExtents();
    else this.fitToVan();
  }

  /** Raum-Stand aus der Kollaboration vollständig übernehmen. */
  private applyRoomDoc(doc: RoomDoc): void {
    const model = Model.fromJSON(doc.model);
    this.applyModelState(model, doc.van, doc.units);
    if (model.vertices.size > 0) this.zoomExtents();
    else this.fitToVan();
  }

  /** Remote-Metadaten (Wohnmobil/Einheit) übernehmen, ohne die Kamera zu bewegen. */
  private applyRemoteMeta(meta: MetaOps): void {
    if (meta.units && meta.units !== this.unitsValue) {
      this.unitsValue = meta.units;
      this.statusBar.setUnits(meta.units);
    }
    if (meta.van && JSON.stringify(meta.van) !== JSON.stringify(this.van)) {
      this.van = meta.van;
      this.panels.syncInputs(this.van);
      this.vanRenderer.update(this.van);
    }
    this.requestUpdate();
  }

  // ---------- Folgen-Modus ----------

  /** Ansicht eines Teilnehmers spiegeln (starten) oder umschalten. */
  private toggleFollow(clientId: string): void {
    if (this.followId === clientId) {
      this.stopFollow('manual');
      return;
    }
    const peer = this.collab?.peers.find((p) => p.clientId === clientId);
    if (peer) this.startFollow(peer);
  }

  private startFollow(peer: PeerInfo): void {
    if (!this.collab) return;
    this.followId = peer.clientId;
    this.topBar.setFollowing(peer.name);
    this.shareOverlay.setPeers(this.collab.peers, this.collab.you, this.followId);
    this.participantsBar.setPeers(this.collab.peers, this.collab.you, this.followId);
    this.statusBar.setStatus(`Folge „${peer.name}“ — eigene Kamerabewegung beendet das Folgen`);
  }

  /**
   * Folgen beenden. 'local' = der Nutzer hat selbst die Kamera bewegt (Orbit,
   * Schwenken, Zoom, Standardansicht …), 'left' = Teilnehmer ist gegangen.
   */
  private stopFollow(reason: 'manual' | 'local' | 'left'): void {
    if (!this.followId) return;
    const name = this.collab?.peers.find((p) => p.clientId === this.followId)?.name ?? '';
    this.followId = null;
    this.topBar.setFollowing(null);
    this.shareOverlay.setPeers(this.collab?.peers ?? [], this.collab?.you ?? null, null);
    this.participantsBar.setPeers(this.collab?.peers ?? [], this.collab?.you ?? null, null);
    this.statusBar.setStatus(
      reason === 'local' ? 'Folgen beendet — du steuerst die Ansicht selbst'
      : reason === 'left' && name ? `„${name}“ hat den Raum verlassen — Folgen beendet`
      : 'Folgen beendet'
    );
  }

  // ---------- Folge-Einladungen ----------

  /** Einen anderen Teilnehmer bitten, der eigenen Ansicht zu folgen. */
  private inviteFollow(clientId: string): void {
    if (!this.collab) return;
    const peer = this.collab.peers.find((p) => p.clientId === clientId);
    if (!peer) return;
    this.collab.inviteFollow(clientId);
    this.participantsBar.markInvited(clientId);
    this.statusBar.setStatus(`Einladung an „${peer.name}“ gesendet — Bestätigung kommt per Popup`);
  }

  /** Alle anderen Teilnehmer einladen (Popup-Bestätigung beim Gegenüber). */
  private inviteAll(): void {
    if (!this.collab || !this.collab.you) return;
    const me = this.collab.you.clientId;
    const others = this.collab.peers.filter((p) => p.clientId !== me);
    if (others.length === 0) {
      this.statusBar.setStatus('Keine anderen Teilnehmer im Raum');
      return;
    }
    for (const p of others) {
      this.collab.inviteFollow(p.clientId);
      this.participantsBar.markInvited(p.clientId);
    }
    this.statusBar.setStatus(`Einladung an ${others.length} Teilnehmer gesendet`);
  }

  /** Einladung erhalten → Bestätigungs-Popup zeigen (sofern wir nicht schon folgen). */
  private showFollowInvite(from: PeerInfo): void {
    if (!this.collab) return;
    if (this.followId === from.clientId) return;
    this.followInviteOverlay.show(from);
  }

  private onFollowInviteResponse(from: PeerInfo, accepted: boolean): void {
    this.statusBar.setStatus(
      accepted ? `„${from.name}“ folgt nun deiner Ansicht` : `„${from.name}“ hat abgelehnt`
    );
  }

  /** Pro Frame: Kamera zum gefolgten Teilnehmer glätten. */
  private stepFollow(): void {    if (!this.followId) return;
    const target = this.collab?.peerView(this.followId) ?? null;
    if (!target) return;
    const next = lerpPeerViewStep(this.camera.captureView(), target, FOLLOW_LERP);
    if (next !== target) {
      // Noch nicht konvergiert → Kamera bewegen (das Zielobjekt selbst = fertig)
      this.camera.applyView(next);
      this.followJustApplied = true;
    }
  }

  /**
   * Eigene Ansicht an die Kollaboration melden. Gleichzeitig nutzt der Aufruf die
   * Ansichts-Änderung, um Folgen zu beenden, sobald der Nutzer selbst die Kamera
   * bewegt — egal über welchen Weg (Umlauf, Mausrad, Ansichts-Knopf, Zoom-Werkzeug).
   */
  private syncOwnView(): void {
    const view = this.camera.captureView();
    if (
      this.followId &&
      !this.followJustApplied &&
      this.lastObservedView &&
      !peerViewEqual(view, this.lastObservedView)
    ) {
      this.stopFollow('local');
    }
    this.lastObservedView = view;
    this.followJustApplied = false;
    this.collab?.syncView(view);
  }

  // ---------- Kollaboration ----------

  /** Session starten (asHost = eigener Stand gilt) oder einem Raum beitreten. */  private startCollab(roomId: string, name: string, asHost: boolean): void {
    this.leaveCollab();
    const manager = new CollabManager(this.collabHooks(), roomId, asHost ? this.currentRoomDoc() : undefined);
    this.collab = manager;
    manager.connect(name);
    const url = new URL(location.href);
    url.searchParams.set('room', roomId);
    history.replaceState(null, '', url);
    this.shareOverlay.setSession(manager.shareUrl);
    this.shareOverlay.show();
  }

  private leaveCollab(): void {
    if (this.followId) this.stopFollow('manual');
    if (this.collab) {
      this.collab.leave();
      this.collab = null;
    }
    this.joinRoomId = null;
    this.presenceRenderer.setSelf(null);
    this.presenceRenderer.clear();
    this.topBar.setCollabStatus('offline', 0, false);
    this.participantsBar.setPeers([], null);
    this.followInviteOverlay.dismiss();
    this.shareOverlay.setSession(null);
    const url = new URL(location.href);
    if (url.searchParams.has('room')) {
      url.searchParams.delete('room');
      history.replaceState(null, '', url);
    }
  }

  private currentRoomDoc(): RoomDoc {
    return { model: this.model.toJSON(), van: this.van, units: this.unitsValue };
  }

  private collabHooks(): CollabHooks {
    return {
      getModel: () => this.model,
      getVan: () => this.van,
      getUnits: () => this.unitsValue,
      loadDoc: (doc) => this.applyRoomDoc(doc),
      applyMeta: (meta) => this.applyRemoteMeta(meta),
      onChanged: () => this.requestUpdate(),
      onStatus: (status, message) => {
        this.topBar.setCollabStatus(status, this.collab?.peers.length ?? 1);
        this.shareOverlay.setStatus(status, message);
        if (status === 'error') {
          // Raum voll o. ä. — Session sauber beenden und im Dialog begründen
          this.leaveCollab();
          this.shareOverlay.setError(message ?? 'Verbindung fehlgeschlagen');
          this.shareOverlay.showJoin();
        }
      },
      onPeers: (peers, you) => {
        this.topBar.setCollabStatus(this.collab?.status ?? 'offline', peers.length);
        this.shareOverlay.setPeers(peers, you, this.followId);
        this.participantsBar.setPeers(peers, you, this.followId);
        this.presenceRenderer.setSelf(you?.clientId ?? null);
        this.presenceRenderer.setPeers(peers, this.collab?.cursors ?? new Map());
        // Gefolgter Teilnehmer ist weg → Folgen sauber beenden
        if (this.followId && !peers.some((p) => p.clientId === this.followId)) {
          this.stopFollow('left');
        }
        // Offene Einladung verfällt, wenn der Einladende gegangen ist
        const inviter = this.followInviteOverlay.inviter;
        if (inviter && !peers.some((p) => p.clientId === inviter.clientId)) {
          this.followInviteOverlay.dismiss();
        }
      },
      onFollowInvite: (msg) => this.showFollowInvite(msg.from),
      onFollowInviteResponse: (msg) => this.onFollowInviteResponse(msg.from, msg.accepted)
    };
  }

  /** Weltpunkt unter dem Cursor für die Presence anderer Nutzer (gedrosselt). */
  private trackPresenceCursor(e: PointerEvent): void {
    if (!this.collab) return;
    const now = performance.now();
    if (now - this.lastPresencePickAt < 100) return;
    this.lastPresencePickAt = now;
    const p = this.toToolPointer(e);
    let cursor: Vec3 | null = this.renderer.pickPoint(p.ndcX, p.ndcY, this.camera.camera);
    if (!cursor) {
      // Kein Treffer auf einer Fläche → Projektion auf den Boden (z = 0)
      const d = p.ray.dir;
      if (Math.abs(d.z) > 1e-6) {
        const t = -p.ray.origin.z / d.z;
        if (t > 0) cursor = { x: p.ray.origin.x + d.x * t, y: p.ray.origin.y + d.y * t, z: 0 };
      }
    }
    this.collab.sendCursor(cursor);
  }

  private restoreAutosave(): void {
    const project = loadAutosave();
    if (!project) return;
    try {
      this.applyProject(project);
    } catch (err) {
      console.error('Autosave konnte nicht geladen werden, wird verworfen:', err);
      clearAutosave();
    }
  }

  /** Nach Modell-Austausch alle Referenzen neu verdrahten. */
  private rebindModel(): void {
    this.inference = new InferenceEngine(this.model, this.camera);
    const oldGroup = this.renderer.group;
    oldGroup.traverse((obj) => {
      const o = obj as THREE.Object3D & { geometry?: THREE.BufferGeometry; material?: THREE.Material | THREE.Material[] };
      o.geometry?.dispose();
      if (Array.isArray(o.material)) o.material.forEach((m) => m.dispose());
      else o.material?.dispose();
    });
    this.renderer = new ModelRenderer(this.model, this.selection);
    this.previewRenderer.clear();
    this.scene.scene.remove(oldGroup);
    this.scene.scene.add(this.renderer.group);
    this.modelDirty = true;
  }

  private refreshVcb(): void {
    const tool = this.toolManager.active;
    if (this.vcbBufferValue) {
      this.statusBar.setVcb(this.vcbBufferValue, true);
    } else {
      this.statusBar.setVcb(tool?.vcbDisplay() ?? null, false);
    }
  }

  private maybeAutosave(force = false): void {
    if (!this.needsSave) return;
    const now = Date.now();
    if (!force && now - this.lastSaveAt < 1500) return;
    this.lastSaveAt = now;
    this.needsSave = false;
    const project = this.currentProjectData();
    const json = JSON.stringify(project);
    if (json === this.lastSavedJson) return;
    this.lastSavedJson = json;
    // Lokaler Crash-Puffer bleibt immer an (auch im Projekt-Modus)
    autosave(project);
    if (this.projectBinding) {
      this.serverDirty = true;
      this.scheduleServerSave(force);
    }
  }

  /**
   * Server-Speicherung drosseln: spätestens alle 2,5 s hochladen, bei `immediate`
   * (Strg+S, Verlassen) ohne Wartezeit.
   */
  private scheduleServerSave(immediate = false): void {
    if (this.serverSaveTimer !== null || this.serverSaveInFlight) return;
    const wait = immediate ? 0 : Math.max(0, SERVER_SAVE_MIN_MS - (Date.now() - this.lastServerSaveAt));
    this.serverSaveTimer = window.setTimeout(() => {
      this.serverSaveTimer = null;
      void this.flushServerSave();
    }, wait);
  }

  /** Aktuellen Stand (inkl. Vorschaubild) in die Projekt-Datenbank schreiben. */
  private async flushServerSave(): Promise<void> {
    if (!this.projectBinding || this.destroyed || this.serverSaveInFlight || !this.serverDirty) return;
    this.serverSaveInFlight = true;
    this.topBar.setSaveState('saving');
    try {
      const project = this.currentProjectData();
      const thumbnail = this.captureThumbnail();
      try {
        await api.saveProject(this.projectBinding.id, { data: project, thumbnail });
        this.lastServerSaveAt = Date.now();
        this.lastServerSavedJson = JSON.stringify(project);
        this.serverDirty = false;
        this.topBar.setSaveState('saved');
      } catch (err) {
        console.error('Projekt konnte nicht gespeichert werden:', err);
        this.topBar.setSaveState('error');
        this.statusBar.setStatus('Speichern fehlgeschlagen — neuer Versuch in wenigen Sekunden');
        this.lastServerSaveAt = Date.now(); // Retry drosseln
        this.serverDirty = true;
        this.scheduleServerSave();
      }
    } finally {
      this.serverSaveInFlight = false;
      // Während des Sendens ist wieder etwas geändert worden → direkt hinterher
      if (this.serverDirty && !this.destroyed) this.scheduleServerSave();
    }
  }

  /** Aktuellen Viewport als 480×270-Vorschaubild (JPEG-DataURL) einfangen. */
  private captureThumbnail(): string | null {
    try {
      if (this.canvas.clientWidth === 0 || this.canvas.clientHeight === 0) return null;
      this.scene.render(this.camera.camera); // frischer Frame im Buffer (kein preserveDrawingBuffer)
      const src = this.canvas;
      const thumb = document.createElement('canvas');
      thumb.width = 480;
      thumb.height = 270;
      const ctx = thumb.getContext('2d');
      if (!ctx) return null;
      ctx.fillStyle = '#dfe7ee';
      ctx.fillRect(0, 0, thumb.width, thumb.height);
      // Mittiger Ausschnitt im 16:9-Cover-Schnitt
      const scale = Math.max(thumb.width / src.width, thumb.height / src.height);
      const sw = thumb.width / scale;
      const sh = thumb.height / scale;
      ctx.drawImage(src, (src.width - sw) / 2, (src.height - sh) / 2, sw, sh, 0, 0, thumb.width, thumb.height);
      return thumb.toDataURL('image/jpeg', 0.72);
    } catch {
      return null;
    }
  }


  /** Aktive Gruppe (Doppelklick = in Gruppe eintreten, Esc = verlassen). */
  activeGroupId(): number | null {
    return this.model.editContext ?? null;
  }

  setActiveGroupId(id: number | null): void {
    if ((this.model.editContext ?? null) === id) return;
    this.model.editContext = id ?? undefined;
    this.requestUpdate();
  }

  templateName(): string {
    return templateById(this.van.templateId).name;
  }

  // ---------- Auswahl-Info (Entity-Info-Panel) ----------

  /** Genau eine geschlossene Gruppe und nichts anderes gewählt → deren ID + Name. */
  private selectedSingleGroup(): { id: number; name: string } | undefined {
    const groups = this.model.groupsAt(this.selection.edges, this.selection.faces);
    if (groups.size !== 1) return undefined;
    const ctx = this.model.editContext;
    for (const id of this.selection.edges) if (this.model.edges.get(id)?.group === ctx) return undefined;
    for (const id of this.selection.faces) if (this.model.faces.get(id)?.group === ctx) return undefined;
    const gid = [...groups][0]!;
    return { id: gid, name: this.model.groups.get(gid)?.name ?? '' };
  }

  /** Liest aus der aktuellen Auswahl die editierbaren Maße ab. */
  private computeSelectionInfo(): SelectionInfo {
    const sel = this.selection;
    if (sel.faces.size > 0) {
      // Einzelne Fläche → zu ihrem zusammenhängenden Körper erweitern
      let faceIds = [...sel.faces];
      if (faceIds.length === 1) {
        faceIds = [...connectedFaceComponent(this.model, faceIds[0]!)];
      }
      const vset = new Set<number>();
      let faceCount = 0;
      for (const fid of faceIds) {
        const f = this.model.faces.get(fid);
        if (!f) continue;
        faceCount++;
        f.outer.forEach((v) => vset.add(v));
        f.holes.forEach((h) => h.forEach((v) => vset.add(v)));
      }
      if (faceCount === 0) return { kind: 'none' };
      const pts = [...vset].map((id) => this.model.vertexPosition(id));
      let minX = Infinity, minY = Infinity, minZ = Infinity, maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
      for (const p of pts) {
        minX = Math.min(minX, p.x); minY = Math.min(minY, p.y); minZ = Math.min(minZ, p.z);
        maxX = Math.max(maxX, p.x); maxY = Math.max(maxY, p.y); maxZ = Math.max(maxZ, p.z);
      }
      return {
        kind: 'shape',
        group: this.selectedSingleGroup(),
        faceCount,
        dims: { x: maxX - minX, y: maxY - minY, z: maxZ - minZ },
        min: { x: minX, y: minY, z: minZ },
        vertexIds: [...vset],
        layer: this.selectionLayer() ?? undefined
      };
    }
    if (sel.edges.size === 1) {
      const e = this.model.edges.get([...sel.edges][0]!);
      if (e) {
        const [a, b] = this.model.edgePoints(e);
        return {
          kind: 'edge',
          edgeId: e.id,
          edgeLength: dist(a, b),
          min: { x: Math.min(a.x, b.x), y: Math.min(a.y, b.y), z: Math.min(a.z, b.z) },
          vertexIds: [e.a, e.b],
          layer: this.selectionLayer() ?? undefined
        };
      }
    }
    return { kind: 'none' };
  }

  private applyEdgeLength(lengthM: number): void {
    const info = this.computeSelectionInfo();
    if (info.kind !== 'edge' || info.edgeId === undefined) return;
    this.beginEdit();
    setEdgeLength(this.model, info.edgeId, lengthM);
    this.requestUpdate();
  }

  private applyDims(d: { x: number; y: number; z: number }): void {
    const info = this.computeSelectionInfo();
    if (info.kind !== 'shape' || !info.dims || !info.min || !info.vertexIds) return;

    // Flache Achse mit neuem Wert → einzelne Fläche entlang +Achse zum Körper extrudieren
    const flatAxis = (['x', 'y', 'z'] as const).find((ax) => info.dims![ax] < 1e-4 && d[ax] > 1e-4);
    const selectedFace = this.selection.faces.size === 1 ? [...this.selection.faces][0]! : null;

    const factor = (cur: number, next: number): number => (cur > 1e-4 && next > 1e-4 ? next / cur : 1);
    this.beginEdit();
    if (flatAxis && selectedFace !== null && info.faceCount === 1) {
      const h = d[flatAxis];
      const delta = flatAxis === 'x' ? vec(h, 0, 0) : flatAxis === 'y' ? vec(0, h, 0) : vec(0, 0, h);
      if (extrudeFace(this.model, selectedFace, delta)) {
        this.requestUpdate();
        return;
      }
      // Extrusion nicht möglich (z. B. degenerierte Fläche) → weiter mit Skalierung
    }
    scaleAboutAnchor(
      this.model,
      info.vertexIds,
      info.min,
      factor(info.dims.x, d.x),
      factor(info.dims.y, d.y),
      factor(info.dims.z, d.z)
    );
    this.requestUpdate();
  }

  /** Auswahl exakt positionieren: Verschiebung, sodass die Mindest-Ecke auf `p` landet. */
  private applyPosition(p: { x: number; y: number; z: number }): void {
    const info = this.computeSelectionInfo();
    if (info.kind === 'none' || !info.min || !info.vertexIds) return;
    const delta = sub(p, info.min);
    if (Math.abs(delta.x) < 1e-6 && Math.abs(delta.y) < 1e-6 && Math.abs(delta.z) < 1e-6) return;
    this.beginEdit();
    moveVertices(this.model, info.vertexIds, delta);
    this.requestUpdate();
  }

  // ---------- Eigene Möbel ----------

  /** Gewählte Gruppe als eigenes Möbel in die Bibliothek aufnehmen. */
  private saveSelectionAsFurniture(name: string): { ok: boolean; message: string } {
    const sel = this.selectedSingleGroup();
    if (!sel) {
      return { ok: false, message: 'Erst genau eine Gruppe auswählen (Klick auf das Möbel · Strg+G gruppiert)' };
    }
    const extracted = extractCustomTemplate(this.model, sel.id);
    if (!extracted) return { ok: false, message: 'Die gewählte Gruppe enthält keine Flächen' };
    const finalName = (name.trim() || sel.name.trim() || 'Eigenes Möbel').slice(0, 40);
    const tpl: CustomFurnitureData = { ...extracted, id: `user-${Date.now().toString(36)}`, name: finalName };
    this.panels.renderCustomFurniture(addCustomFurniture(tpl));
    this.needsSave = true;
    return { ok: true, message: `„${finalName}“ gespeichert — in „Eigene Möbel“ stellbar` };
  }

  // ---------- Ebenen (Sichtbarkeit / Deckkraft) ----------

  /** Zeilen des Ebenen-Panels: lose Modellebene (falls vorhanden) + oberste Gruppen. */
  private computeLayers(): LayerInfo[] {
    const list: LayerInfo[] = [];
    let hasLoose = [...this.model.edges.values()].some((e) => e.group === undefined) ||
      [...this.model.faces.values()].some((f) => f.group === undefined);
    if (hasLoose) {
      list.push({
        key: 'loose', groupId: null, name: 'Losgeometrie',
        visible: this.model.looseVisible, opacity: this.model.looseOpacity
      });
    }
    for (const g of this.model.groups.values()) {
      if (g.parent !== undefined) continue;
      const ent = this.model.entitiesInGroup(g.id);
      if (ent.edges.length === 0 && ent.faces.length === 0) continue;
      list.push({
        key: `g${g.id}`, groupId: g.id, name: g.name || 'Gruppe',
        visible: g.visible !== false, opacity: g.opacity ?? 1
      });
    }
    return list;
  }

  private toggleLayerVisible(target: LayerTarget): void {
    this.beginEdit();
    if (target.kind === 'loose') {
      this.model.looseVisible = !this.model.looseVisible;
    } else {
      const g = this.model.groups.get(target.id);
      if (!g) return;
      if (g.visible === false) g.visible = true;
      else this.hideGroup(target.id);
    }
    this.requestUpdate();
  }

  /** Blendet eine oberste Gruppe aus: Auswahl darin entfernen, offene Gruppe schließen. */
  private hideGroup(gid: number): void {
    const g = this.model.groups.get(gid);
    if (!g || g.visible === false) return;
    g.visible = false;
    const sub = this.model.subtreeGroups(gid);
    for (const id of [...this.selection.edges]) {
      const gr = this.model.edges.get(id)?.group;
      if (gr !== undefined && sub.has(gr)) this.selection.edges.delete(id);
    }
    for (const id of [...this.selection.faces]) {
      const gr = this.model.faces.get(id)?.group;
      if (gr !== undefined && sub.has(gr)) this.selection.faces.delete(id);
    }
    if (this.model.editContext !== undefined && sub.has(this.model.editContext)) {
      this.model.editContext = undefined;
      this.selection.clear();
    }
  }

  /** Ebene der aktuellen Auswahl — Deckkraft wird als Objekt-Eigenschaft daran editiert. */
  private selectionLayer(): { groupId: number | null; opacity: number } | null {
    if (this.selection.isEmpty) return null;
    const groups = this.model.groupsAt(this.selection.edges, this.selection.faces);
    const hasLoose = [...this.selection.edges].some((id) => this.model.edges.get(id)?.group === this.model.editContext) ||
      [...this.selection.faces].some((id) => this.model.faces.get(id)?.group === this.model.editContext);
    let groupId: number | null;
    if (groups.size === 1 && !hasLoose) {
      groupId = this.model.topLevelGroup([...groups][0]!) ?? null;
    } else if (groups.size === 0 && hasLoose) {
      groupId = this.model.editContext === undefined ? null : this.model.topLevelGroup(this.model.editContext) ?? null;
    } else {
      return null; // gemischt oder mehrere Ebenen → keine eindeutige Objekt-Eigenschaft
    }
    const opacity = groupId === null ? this.model.looseOpacity : this.model.groups.get(groupId)?.opacity ?? 1;
    return { groupId, opacity };
  }

  /** Klick auf einen Ebenennamen: Objekt in der Szene wählen. */
  private selectLayer(target: LayerTarget): void {
    if (target.kind === 'loose') {
      const ctx = this.model.editContext;
      this.selection.set(
        [...this.model.edges.values()].filter((e) => e.group === ctx).map((e) => e.id),
        [...this.model.faces.values()].filter((f) => f.group === ctx).map((f) => f.id)
      );
    } else {
      const ent = this.model.entitiesInGroup(target.id);
      this.selection.set(ent.edges, ent.faces);
    }
    this.requestUpdate();
  }

  private showAllLayers(): void {
    let changed = !this.model.looseVisible;
    for (const g of this.model.groups.values()) {
      if (g.visible === false) { g.visible = true; changed = true; }
    }
    if (!changed) return;
    this.beginEdit();
    this.model.looseVisible = true;
    this.requestUpdate();
  }

  /** Kontextmenü „Ausblenden“: Gewähltes auf seine Ebenen abgebildet ausblenden. */
  private hideSelection(): void {
    if (this.selection.isEmpty) return;
    this.beginEdit();
    const groups = this.model.groupsAt(this.selection.edges, this.selection.faces);
    const hasLoose = [...this.selection.edges].some((id) => this.model.edges.get(id)?.group === this.model.editContext) ||
      [...this.selection.faces].some((id) => this.model.faces.get(id)?.group === this.model.editContext);
    for (const gid of groups) {
      const top = this.model.topLevelGroup(gid);
      if (top !== undefined) this.hideGroup(top);
    }
    if (hasLoose && groups.size === 0 && this.model.editContext === undefined) {
      this.model.looseVisible = false;
      this.selection.clear();
    }
    this.requestUpdate();
  }
}
